/**
 * Quick add to cart for product cards (snippets/card.liquid).
 *
 * This module owns ALL quick-add behaviour for cards: click handling, the
 * add-to-cart request, the minicart open, and the mobile size-picker drawer
 * (snippets/quick-add-drawer.liquid). It is fully decoupled from the wishlist.
 *
 * Card controls live inside the card's <a href>, so every click in the
 * quick-add zone is preventDefault-ed here to stop navigation to the PDP.
 *
 * Wishlist integration is event-based: on a successful add we publish
 * PUB_SUB_EVENTS.cartUpdate with source 'quick-add'. wishlist.js listens and
 * removes the product from the wishlist (remove-on-add) when applicable.
 *
 * Globals consumed (all defined before this script runs):
 *   fetchConfig (base.js), publish / PUB_SUB_EVENTS (pubsub.js / constants.js),
 *   window.routes, window.variantStrings, window.cartStrings (head-init.liquid),
 *   window.OptionTypeUtils (global.js), window.minicart (minicart.js),
 *   window.sizeChartHelpers (size-chart.js).
 */
(function () {
  const SELECTORS = {
    card: '.brn-card',
    // Any element inside a card whose click must never navigate to the PDP.
    zone: '.add__to__cart, .add__to__cart--desktop, .variants-container, .variant-elem',
    addButton: '.add__to__cart',
    variantElem: '.variant-elem',
    drawer: '#quick-add-drawer',
    drawerHandle: '#quick-add-drawer .quick-add-drawer__handle',
    drawerList: '#quick-add-drawer .quick-add-drawer__content ul',
    drawerItem: '#quick-add-drawer li',
    overlay: '.quick-add-overlay',
    sizeGuideTarget: '.quick-add-drawer__size-guide',
    heightTarget: '.quick-add-drawer__height',
    sizingTarget: '.quick-add-drawer__sizing',
  };

  // Size-guide HTML cache keyed by product handle (shared by prefetch + open).
  const sizeGuideCache = new Map();

  function init() {
    document.addEventListener('click', onClick);
    document.addEventListener('mouseover', onVariantHover);
    document.addEventListener('mouseout', onVariantHoverOut);
    initSizeGuidePrefetch();
  }

  // ---------------------------------------------------------------------------
  // Click routing
  // ---------------------------------------------------------------------------
  function onClick(e) {
    // Drawer close affordances.
    if (e.target.closest(SELECTORS.overlay)) {
      e.preventDefault();
      closeDrawer();
      return;
    }
    if (e.target.closest(SELECTORS.drawerHandle)) {
      closeDrawer();
      return;
    }

    // Size tapped inside the mobile drawer.
    const drawerItem = e.target.closest(SELECTORS.drawerItem);
    if (drawerItem) {
      e.preventDefault();
      if (drawerItem.getAttribute('aria-available') === 'true') {
        addToCart(drawerItem.dataset.variantId, { productId: drawerItem.dataset.productId });
        closeDrawer();
      }
      return;
    }

    // Card quick-add controls. Anything in the zone must not follow the link.
    const zone = e.target.closest(SELECTORS.zone);
    if (!zone || !zone.closest(SELECTORS.card)) return;
    e.preventDefault();

    // Inline size button (desktop hover panel).
    const variantElem = e.target.closest(SELECTORS.variantElem);
    if (variantElem) {
      if (variantElem.getAttribute('aria-available') === 'true') {
        addToCart(variantElem.dataset.variantId, { productId: variantElem.dataset.productId });
      }
      return;
    }

    // Add button (tablet "+" / mobile row / desktop-single). Clicks that land on
    // the .add__to__cart--desktop wrapper padding fall through here as a no-op
    // (navigation already prevented above), which is the desired behaviour.
    const addBtn = e.target.closest(SELECTORS.addButton);
    if (!addBtn) return;

    if (addBtn.dataset.single === 'true') {
      addToCart(addBtn.dataset.firstVariant, { productId: addBtn.dataset.productId });
    } else {
      openDrawerForProduct(addBtn.dataset.productId, addBtn.dataset.productHandle);
    }
  }

  // Swap the desktop panel label to "sold out" while hovering an unavailable size.
  function onVariantHover(e) {
    const el = e.target.closest('.brn-card .variant-elem');
    if (!el || el.getAttribute('aria-available') !== 'false') return;
    const label = el.closest('.add__to__cart--desktop')?.querySelector('p');
    if (label) label.textContent = window.variantStrings?.prodSoldOut || 'Agotado';
  }

  function onVariantHoverOut(e) {
    const el = e.target.closest('.brn-card .variant-elem');
    if (!el || el.getAttribute('aria-available') !== 'false') return;
    const label = el.closest('.add__to__cart--desktop')?.querySelector('p');
    if (label) label.textContent = window.variantStrings?.addToCart || 'Añadir a la cesta';
  }

  // ---------------------------------------------------------------------------
  // Mobile size drawer
  // ---------------------------------------------------------------------------
  // Inline path (in-card controls): sizes come from the `variants-<id>` JSON.
  function openDrawerForProduct(productId, productHandle) {
    if (!document.querySelector(SELECTORS.drawer)) return;
    populateSizes(productId);
    if (productHandle) loadSizeGuide(productHandle);
    showDrawer();
  }

  // Fetch path (<quick-add> element): sizes come from a fetched product payload.
  function openDrawerFromProduct(product) {
    if (!document.querySelector(SELECTORS.drawer)) return;
    populateSizesFromProduct(product);
    if (product.handle) loadSizeGuide(product.handle);
    showDrawer();
  }

  function showDrawer() {
    const drawer = document.querySelector(SELECTORS.drawer);
    if (!drawer) return;
    drawer.setAttribute('aria-expanded', 'true');
    drawer.setAttribute('cart-process', 'open');
    document.querySelector(SELECTORS.overlay)?.setAttribute('aria-overlay', 'open');
  }

  function closeDrawer() {
    document.querySelector(SELECTORS.drawer)?.setAttribute('aria-expanded', 'false');
    document.querySelector(SELECTORS.overlay)?.setAttribute('aria-overlay', 'close');
  }

  // Render the drawer size list from a normalized list of
  // { id, title, available, productId } items.
  function renderSizeItems(items) {
    const list = document.querySelector(SELECTORS.drawerList);
    if (!list) return;

    const unavailableLabel = (window.variantStrings?.unavailable_with_option || '')
      .replace('[value]', window.variantStrings?.size || '')
      .replace('[', '')
      .replace(']', '');

    list.innerHTML = items
      .filter((item) => item.title)
      .map(
        (item) =>
          `<li aria-available="${item.available}" data-product-id="${item.productId}" data-variant-id="${item.id}"><span>${item.title}</span><span>${unavailableLabel}</span></li>`
      )
      .join('');
  }

  // Inline path: read the `variants-<id>` JSON emitted by card.liquid.
  function populateSizes(productId) {
    const json = document.getElementById(`variants-${productId}`)?.textContent;
    if (!json) return;

    let variants;
    try {
      variants = JSON.parse(json).reverse();
    } catch {
      return;
    }

    renderSizeItems(
      variants.map((variant) => {
        const size = variant.selectedOptions?.find((opt) => window.OptionTypeUtils?.isSize(opt.name));
        return {
          id: variant.id.split('/').pop(),
          title: size ? size.value : variant.title,
          available: variant.availableForSale,
          productId,
        };
      })
    );
  }

  // Fetch path (used by the <quick-add> element): build the list from the
  // /products/<handle>.js payload. Variants are size-only in this theme, so the
  // size option value is the variant label.
  function populateSizesFromProduct(product) {
    const optionNames = (product.options || []).map((opt) => (typeof opt === 'string' ? opt : opt.name));
    const sizeIndex = optionNames.findIndex((name) => window.OptionTypeUtils?.isSize(name));

    renderSizeItems(
      (product.variants || []).map((variant) => ({
        id: variant.id,
        title: sizeIndex >= 0 ? variant.options[sizeIndex] : variant.title,
        available: variant.available,
        productId: product.id,
      }))
    );
  }

  // ---------------------------------------------------------------------------
  // Size guide (fetched from the product page, same approach as before)
  // ---------------------------------------------------------------------------
  function initSizeGuidePrefetch() {
    if (!('IntersectionObserver' in window)) return;
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          const btn = entry.target;
          if (btn.dataset.single === 'false' && btn.dataset.productHandle) {
            prefetchSizeGuide(btn.dataset.productHandle);
          }
          observer.unobserve(btn);
        });
      },
      { rootMargin: '50px' }
    );
    document
      .querySelectorAll('.brn-card .add__to__cart[data-single="false"][data-product-handle]')
      .forEach((btn) => observer.observe(btn));
  }

  async function prefetchSizeGuide(handle) {
    if (sizeGuideCache.has(handle)) return;
    try {
      const res = await fetch(`/products/${handle}`);
      if (res.ok) sizeGuideCache.set(handle, await res.text());
    } catch {
      /* best-effort prefetch */
    }
  }

  async function loadSizeGuide(handle) {
    try {
      let html = sizeGuideCache.get(handle);
      if (!html) {
        const res = await fetch(`/products/${handle}`);
        if (!res.ok) return;
        html = await res.text();
        sizeGuideCache.set(handle, html);
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const source = doc.getElementById('size_guide_container');
      const target = document.querySelector(SELECTORS.sizeGuideTarget);
      if (!source || !target) return;

      target.innerHTML = source.innerHTML;

      const heightSource = doc.querySelector('.brn-product__model');
      const sizingSource = doc.querySelector('.brn-product__sizing');
      const heightTarget = document.querySelector(SELECTORS.heightTarget);
      const sizingTarget = document.querySelector(SELECTORS.sizingTarget);
      if (heightSource && heightTarget) heightTarget.innerHTML = heightSource.innerHTML;
      if (sizingSource && sizingTarget) sizingTarget.innerHTML = sizingSource.innerHTML;

      if (window.sizeChartHelpers) {
        window.sizeChartHelpers.initSizeChartTabsFromSource();
        window.sizeChartHelpers.buildSizeChartPanesFromSource();
        window.sizeChartHelpers.initSizeChartTabs();
      }
    } catch (error) {
      console.error('[quick-add] size guide error:', error);
    }
  }

  // ---------------------------------------------------------------------------
  // Add to cart
  // ---------------------------------------------------------------------------
  async function addToCart(variantId, ctx = {}) {
    if (!variantId) return;

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    delete config.headers['Content-Type'];

    const formData = new FormData();
    formData.append('id', variantId);
    formData.append('quantity', 1);

    const cartDrawer = document.querySelector('cart-drawer');
    const sectionIds = cartDrawer?.getSectionsToRender?.().map((section) => section.id) || [];
    if (sectionIds.length) {
      formData.append('sections', sectionIds.join(','));
      formData.append('sections_url', window.location.pathname);
    }
    config.body = formData;

    try {
      const response = await fetch(`${window.routes.cart_add_url}.js`, config);
      const data = await response.json();

      if (!response.ok || data.status) {
        console.error('[quick-add] add to cart failed:', data?.description || data?.message || response.status);
        publish(PUB_SUB_EVENTS.cartError, {
          source: 'quick-add',
          productVariantId: variantId,
          errors: data?.description,
          message: data?.message,
        });
        // Single-variant cards have no in-card stock cue, so surface failures
        // (almost always sold out — 422) through the unified toaster.
        const soldOut = response.status === 422;
        window.toast?.show(
          soldOut ? window.variantStrings?.soldOut || 'Agotado' : window.cartStrings?.error || data?.description || 'Error',
          { type: 'error', icon: soldOut ? 'info' : 'error' }
        );
        return;
      }

      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'quick-add',
        productVariantId: variantId,
        productId: ctx.productId,
        cartData: data,
      });

      const itemCount = updateCartBubble(data);
      openMinicart(data, itemCount);
    } catch (error) {
      console.error('[quick-add] add to cart error:', error);
    }
  }

  // Refresh the header cart bubble from the returned section, and return the
  // parsed total item count (or undefined when unavailable).
  function updateCartBubble(data) {
    const sectionHtml = data?.sections?.['cart-icon-bubble'];
    if (!sectionHtml) return undefined;

    const parsed = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const newBubble = parsed.querySelector('.shopify-section');
    const bubbleElement = document.getElementById('cart-icon-bubble');
    if (newBubble && bubbleElement) {
      bubbleElement.innerHTML = newBubble.innerHTML;
    }

    const count = Number(parsed.querySelector('.wt-header__panel__counter')?.textContent?.trim());
    return Number.isFinite(count) ? count : undefined;
  }

  function openMinicart(data, itemCount) {
    if (!window.minicart || typeof window.minicart.open !== 'function') return;

    const lang = document.documentElement.lang || 'es-ES';
    const locale = lang.toLowerCase().includes('es') ? 'es-ES' : lang;
    const priceCents = data.final_price ?? data.price ?? 0;
    const priceText = new Intl.NumberFormat(locale, { style: 'currency', currency: 'EUR' }).format(priceCents / 100);

    const selectedSize = data.options_with_values?.find((o) => /size|talla/i.test(o?.name || ''))?.value;
    const title = selectedSize
      ? window.cartStrings.addedToCartSize.replace('__size__', selectedSize)
      : window.cartStrings.addedToCart;

    window.minicart.open({
      title,
      productTitle: data.product_title,
      imageSrc: data.image,
      imageAlt: data.title,
      priceText,
      variantText: data.variant_title,
      itemCount,
    });
  }

  // ---------------------------------------------------------------------------
  // <quick-add> custom element
  //
  // The trigger used outside product cards (upsells, shoppable, collection-list
  // cards). It wraps a button and delegates to the same engine: resolve the
  // product from /products/<handle>.js, then add directly (single variant) or
  // open the size drawer (multi). Replaces the old quick-view (quick-add.js).
  // ---------------------------------------------------------------------------
  async function fetchProduct(handle) {
    try {
      const res = await fetch(`/products/${handle}.js`);
      if (!res.ok) return null;
      return await res.json();
    } catch {
      return null;
    }
  }

  if (!customElements.get('quick-add')) {
    customElements.define(
      'quick-add',
      class QuickAdd extends HTMLElement {
        connectedCallback() {
          this.trigger = this.querySelector('button');
          if (!this.trigger || this.trigger.dataset.quickAddBound) return;
          this.trigger.dataset.quickAddBound = 'true';
          this.trigger.addEventListener('click', (e) => this.onClick(e));
        }

        async onClick(e) {
          e.preventDefault();
          e.stopPropagation();
          const handle = this.dataset.productHandle;
          if (!handle) return;

          const product = await fetchProduct(handle);
          const variants = product?.variants || [];
          if (!variants.length) return;

          if (variants.length === 1) {
            addToCart(String(variants[0].id), { productId: product.id });
          } else {
            openDrawerFromProduct(product);
          }
        }
      }
    );
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
